import React, { useEffect } from 'react';
import {
  Search,
  Sparkles,
  BarChart3,
  TrendingUp,
  Network,
  Compass,
  Check,
  Mail,
  Calendar,
} from 'lucide-react';
import content from '../content/consultingPage.json';
import ConsentBanner from '../components/ConsentBanner';
import { trackEvent } from '../lib/analytics';

// Neural-network visualization (data & AI). Layered nodes with flowing signals.
const NN_LAYERS = [
  { x: 48, ys: [70, 125, 180] },
  { x: 165, ys: [48, 103, 158, 213] },
  { x: 282, ys: [98, 153] },
];

const NeuralNet = () => {
  const edges = [];
  for (let l = 0; l < NN_LAYERS.length - 1; l += 1) {
    const a = NN_LAYERS[l];
    const b = NN_LAYERS[l + 1];
    a.ys.forEach((ay) => {
      b.ys.forEach((by) => {
        edges.push({ x1: a.x, y1: ay, x2: b.x, y2: by });
      });
    });
  }
  const nodes = NN_LAYERS.flatMap((layer) => layer.ys.map((y) => ({ x: layer.x, y })));

  return (
    <div className="cpage-nn" aria-hidden="true">
      <svg viewBox="0 0 330 260" role="img">
        <defs>
          <linearGradient id="nnGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1D7FE8" />
            <stop offset="100%" stopColor="#0F9F95" />
          </linearGradient>
        </defs>
        <g className="nn-edges">
          {edges.map((e, i) => (
            <line
              key={i}
              className="nn-line"
              x1={e.x1}
              y1={e.y1}
              x2={e.x2}
              y2={e.y2}
              style={{ animationDelay: `${(i % 7) * -0.34}s` }}
            />
          ))}
        </g>
        <g className="nn-nodes">
          {nodes.map((n, i) => (
            <g key={i} style={{ animationDelay: `${(i % 5) * 0.3}s` }}>
              <circle className="nn-halo" cx={n.x} cy={n.y} r="11" style={{ animationDelay: `${(i % 5) * 0.3}s` }} />
              <circle className="nn-node" cx={n.x} cy={n.y} r="6.5" style={{ animationDelay: `${(i % 5) * 0.3}s` }} />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

const ICONS = { Search, Sparkles, BarChart3, TrendingUp, Network, Compass };

const Eyebrow = ({ icon: Icon, children }) => (
  <div className="section-label">
    {Icon ? <Icon size={18} /> : null}
    <span>{children}</span>
  </div>
);

const ConsultingPage = () => {
  const { brand, bookingUrl, email, altEmail, ctaLabel, hero, intro, proof, services, audience, process: proc, why, outcomes, cta } = content;

  useEffect(() => {
    // v2-root is set statically in index.html (prevents first-paint flash)
    document.documentElement.classList.add('v2-root');
    document.title = `${brand} — Data & AI Consulting`;
  }, [brand]);

  // Scroll-reveal animation for section titles
  useEffect(() => {
    const titles = Array.from(
      document.querySelectorAll('.cpage .section-title, .cpage .cpage-final-title')
    );
    if (titles.length === 0) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      titles.forEach((t) => t.classList.add('cpage-title-reveal', 'is-revealed'));
      return undefined;
    }

    titles.forEach((t) => t.classList.add('cpage-title-reveal'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    titles.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Staggered reveal for card grids
  useEffect(() => {
    const groups = Array.from(
      document.querySelectorAll(
        '.cpage-services-grid, .cpage-steps, .cpage-audience-grid, .cpage-outcomes-grid'
      )
    );
    if (groups.length === 0) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      groups.forEach((g) => g.classList.add('cpage-stagger', 'is-in'));
      return undefined;
    }
    groups.forEach((g) => g.classList.add('cpage-stagger'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    groups.forEach((g) => io.observe(g));
    return () => io.disconnect();
  }, []);

  // Top scroll-progress bar
  useEffect(() => {
    const bar = document.getElementById('cpage-progress');
    if (!bar) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const p = max > 0 ? Math.min(1, el.scrollTop / max) : 0;
      bar.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const mailtoHref = `mailto:${email}?subject=Data%20%26%20AI%20Consulting%20Enquiry`;

  return (
    <div className="App cpage" id="top">
      <div id="cpage-progress" className="cpage-progress" aria-hidden="true" />

      {/* Hero */}
      <section className="cpage-section cpage-hero">
        <div className="cpage-hero-aura" aria-hidden="true" />
        <h1 className="cpage-hero-title cpage-rise" style={{ '--d': '0.05s' }}>
          {hero.titleLead ? (
            <>
              <span className="cpage-hero-lead-line">{hero.titleLead}</span>
              <span className="cpage-hero-accent">{hero.titleAccent}</span>
            </>
          ) : (
            hero.title
          )}
        </h1>
      </section>

      {/* Intro split: bio + proof (left)  ·  pitch + CTA (right) */}
      <section className="cpage-section cpage-intro">
        <div className="cpage-intro-grid">
          <div className="cpage-intro-left cpage-rise" style={{ '--d': '0.18s' }}>
            <div className="cpage-intro-photo">
              <img src={intro.photo} alt={intro.name} width="120" height="120" />
            </div>
            <div className="cpage-intro-bio">
              {intro.bio.map((p, i) => {
                if (i === 0 && intro.name && p.includes(intro.name)) {
                  const [pre, post] = p.split(intro.name);
                  return (
                    <p key={i} className="cpage-intro-bio-lead">
                      {pre}
                      <span className="cpage-name">{intro.name}</span>
                      {post}
                    </p>
                  );
                }
                return (
                  <p key={i} className={i === 0 ? 'cpage-intro-bio-lead' : ''}>{p}</p>
                );
              })}
            </div>

            <div className="cpage-proof">
              {proof.items.map((it, i) => (
                <React.Fragment key={it.label}>
                  {i > 0 && <span className="cpage-proof-div" aria-hidden="true" />}
                  <div className="cpage-proof-item">
                    <span className="cpage-proof-value">{it.value}</span>
                    <span className="cpage-proof-label">{it.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="cpage-intro-right cpage-rise" style={{ '--d': '0.3s' }}>
            <NeuralNet />
            <div className="cpage-intro-pitch">
              {intro.pitch.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="cpage-hero-cta">
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" onClick={() => trackEvent("book_consultation_click")}>
                <span>{ctaLabel}</span>
                <Calendar size={16} />
              </a>
              <a href={mailtoHref} className="btn-secondary" onClick={() => trackEvent("email_click")}>
                <span>Email Me</span>
                <Mail size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="cpage-section">
        <div className="cpage-head">
          <Eyebrow icon={Sparkles}>{services.eyebrow}</Eyebrow>
          <h2 className="section-title">{services.title}</h2>
        </div>
        <div className="cpage-services-grid">
          {services.items.map((svc) => {
            const Icon = ICONS[svc.icon] || Sparkles;
            return (
              <article className="cpage-service-card" key={svc.title}>
                <div className="cpage-service-icon">
                  <Icon size={22} />
                </div>
                <h3 className="cpage-service-title">{svc.title}</h3>
                <p className="cpage-service-desc">{svc.description}</p>
                {svc.outcomes && svc.outcomes.length > 0 && (
                  <ul className="cpage-outcome-list">
                    {svc.outcomes.map((o) => (
                      <li key={o}>
                        <Check size={14} aria-hidden="true" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* Who I work with */}
      <section className="cpage-section">
        <div className="cpage-head">
          <Eyebrow icon={Network}>{audience.eyebrow}</Eyebrow>
          <h2 className="section-title">{audience.title}</h2>
          <p className="cpage-lead">{audience.lead}</p>
        </div>
        <div className="cpage-audience-grid">
          {audience.clients.map((c) => (
            <div className="cpage-audience-item" key={c}>
              <Check size={15} aria-hidden="true" />
              <span>{c}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How I work */}
      <section className="cpage-section">
        <div className="cpage-head">
          <Eyebrow icon={Compass}>{proc.eyebrow}</Eyebrow>
          <h2 className="section-title">{proc.title}</h2>
        </div>
        <div className="cpage-steps">
          {proc.steps.map((step, i) => (
            <div className="cpage-step" key={step.title}>
              <div className="cpage-step-num">{String(i + 1).padStart(2, '0')}</div>
              <div className="cpage-step-body">
                <h3 className="cpage-step-title">{step.title}</h3>
                <p className="cpage-step-desc">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why work with me */}
      <section className="cpage-section">
        <div className="cpage-why">
          <div className="cpage-why-text">
            <Eyebrow icon={Sparkles}>{why.eyebrow}</Eyebrow>
            <h2 className="section-title">{why.title}</h2>
            {why.paragraphs.map((p, i) => (
              <p className="cpage-why-p" key={i}>{p}</p>
            ))}
          </div>
          <div className="cpage-why-tags">
            {why.tags.map((t) => (
              <span className="cpage-tag" key={t}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Typical outcomes */}
      <section className="cpage-section">
        <div className="cpage-head">
          <Eyebrow icon={TrendingUp}>{outcomes.eyebrow}</Eyebrow>
          <h2 className="section-title">{outcomes.title}</h2>
        </div>
        <div className="cpage-outcomes-grid">
          {outcomes.items.map((o) => (
            <div className="cpage-outcome-chip" key={o}>
              <Check size={15} aria-hidden="true" />
              <span>{o}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="cpage-section cpage-final-wrap">
        <div className="cpage-final">
          <Eyebrow icon={Mail}>{cta.eyebrow}</Eyebrow>
          <h2 className="cpage-final-title">{cta.title}</h2>
          <p className="cpage-final-lead">{cta.lead}</p>
          <div className="cpage-hero-cta">
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" onClick={() => trackEvent("book_consultation_click")}>
              <span>{ctaLabel}</span>
              <Calendar size={16} />
            </a>
            <a href={mailtoHref} className="btn-secondary" onClick={() => trackEvent("email_click")}>
              <span>{email}</span>
              <Mail size={16} />
            </a>
          </div>
          {altEmail && (
            <p className="cpage-final-alt">
              Or email me directly at{' '}
              <a href={`mailto:${altEmail}?subject=Data%20%26%20AI%20Consulting%20Enquiry`}>{altEmail}</a>
            </p>
          )}
        </div>
      </section>

      <footer className="footer cpage-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-text">
              <span>© {new Date().getFullYear()} {brand}. All rights reserved.</span>
            </div>
            <a href="/privacy.html" className="footer-privacy">Privacy</a>
          </div>
        </div>
      </footer>
      <ConsentBanner />
    </div>
  );
};

export default ConsultingPage;
